<template>
    <div class="signUp">
        <div class="header">
            <a :href="links.ironsrc" target="_self" class="logo" v-once>
                <IconBase width="150" height="36" :iconColor="consts.COLORS_V2.DARK_500"
                          icon-name="companyLogoIcon">
                </IconBase>
            </a>
            <div class="body loginLabel">
                Already have an account?
            </div>
            <router-link to="/login" tag="span"
                  class="loginButton body">
                LOG IN
                <span class="loginButtonHovered-background">
                    <span class="loginButtonHovered-text">
                        LOG IN
                    </span>
                </span>
            </router-link>
        </div>
        <div class="content">
            <div class="imageAndText">
                <div class="spaceship">
                    <img src="@/assets/gifs/spaceship.gif"
                         alt="fly around the world"
                         width="270"
                         height="220"
                    >
                </div>
                <div class="titleGiant growBusiness">
                    Grow your app business with ironSource
                </div>
            </div>
            <div class="form">
                <FormInput v-model="userData.firstName"
                           id="firstName"
                           placeholder="First Name"
                           :maxLength="32"
                           :showValidationErrors="showValidationErrors"
                           :customValidations="[{ validationFunc: isOnlyAsciiChars,
                                                  errorMsg: noSpecialCharsErr}]"
                           @changedValue="inputValueChange($event, 'firstName')"
                           :isRequired="true"
                ></FormInput>
                <FormInput v-model="userData.lastName"
                           id="lastName"
                           placeholder="Last Name"
                           :maxLength="32"
                           :showValidationErrors="showValidationErrors"
                           :customValidations="[{ validationFunc: isOnlyAsciiChars,
                                                  errorMsg: noSpecialCharsErr}]"
                           @changedValue="inputValueChange($event,'lastName')"
                           :isRequired="true"
                ></FormInput>
                <FormInput v-model="userData.email"
                           id="email"
                           placeholder="Email"
                           class="fullRow email"
                           :maxLength="64"
                           :showValidationErrors="showValidationErrors"
                           :pattern="emailRegex"
                           :patternError="emailValidationErr"
                           @changedValue="inputValueChange($event, 'email')"
                           :isRequired="true"
                ></FormInput>
                <FormInput v-model="userData.password"
                           id="password"
                           placeholder="Set a password"
                           inputType="password"
                           :showValidationErrors="showValidationErrors"
                           :maxLength="20"
                           :isPassHidden="isPassHidden"
                           @changedValue="inputValueChange($event, 'password')"
                           @showPasswordChanged="showPasswordChanged"
                           :isRequired="true"
                           :customValidations="[
                               { validationFunc: passwordRegexFunction, errorMsg: passwordValidationErr}
                           ]"
                ></FormInput>
                <FormInput v-model="userData.confirmPassword"
                           id="confirmPassword"
                           placeholder="Confirm password"
                           inputType="password"
                           :showValidationErrors="showValidationErrors"
                           :maxLength="20"
                           :isPassHidden="isPassHidden"
                           @changedValue="inputValueChange($event, 'confirmPassword')"
                           @showPasswordChanged="showPasswordChanged"
                           :customValidations="[
                               { validationFunc: passwordsMatch, errorMsg: passwordsMatchErr},
                               { validationFunc: passwordRegexFunction, errorMsg: passwordValidationErr}
                           ]"
                           :triggerCustomValidationOnChange="userData.password"
                           :isRequired="true"
                ></FormInput>
                <FormInput v-model="userData.companyName"
                           id="companyName"
                           placeholder="Company Name"
                           class="fullRow companyName"
                           :showValidationErrors="showValidationErrors"
                           @changedValue="inputValueChange($event,'companyName')"
                           :maxLength="32"
                           :customValidations="[{ validationFunc: isOnlyAsciiChars,
                                                  errorMsg: noSpecialCharsErr}]"
                           :isRequired="true"
                ></FormInput>
                <FormInput v-model="userData.city"
                           id="city"
                           placeholder="City"
                           :showValidationErrors="showValidationErrors"
                           :maxLength="40"
                           :customValidations="[{ validationFunc: isOnlyAsciiChars,
                                                  errorMsg: noSpecialCharsErr},
                                                { validationFunc: checkNoNumeric,
                                                  errorMsg: 'Should not have numbers'}]"
                           @changedValue="inputValueChange($event, 'city')"
                ></FormInput>
                <DropdownSearch id="country"
                                :options="countries" :selectedId="userData.country"
                                placeholder="Country"
                                :showValidationErrors="showValidationErrors"
                                @changedValue="countryDropdownValueChange"></DropdownSearch>
                <span class="fullRow systemErrorMessage">
                    <SystemMessage :message="errorMessage" @acknowledged="errorMessage = ''"></SystemMessage>
                </span>
                <div class="fullRow noUpdate">
                    <input id="noUpdates" type="checkbox" v-model="userData.noUpdates">
                    <label for="noUpdates" class="body checkboxLabel">
                        I don’t want to receive updates with best practices from industry leaders
                    </label>
                </div>
                <div class="fullRow termsAndConds">
                    <input id="termsAndConditions" type="checkbox" v-model="userData.termsAndConditions">
                    <label for="termsAndConditions"
                           class="body checkboxLabel">
                        By signing up you agree to
                        <a class="grey" target="_blank" :href="links.termsNewUser">Publishers Terms & Conditions</a> and
                        <a class="grey" target="_blank" :href="links.termsAdvertisers">Advertisers Terms & Conditions</a>
                    </label>
                    <div v-if="showValidationErrors && !userData.termsAndConditions"
                         class="errorMessage caption">
                        {{ mandatoryError }}
                    </div>
                </div>
                <div class="fullRow submitRow relativeContainer">
                    <div class="captcha">
                        <Captcha @captchaCallback="onCallbackCaptchaResponse" v-if="isCaptchaDisplayed"
                                 @captchaExpiredCallback="onCallbackExpiredCaptcha" captchaId="signUpCaptcha">
                        </Captcha>
                    </div>
                    <div class="error errorMessage bodyStrong"
                         v-if="captchaRequiredError">Approve you are not a robot</div>
                    <button type="submit" class="headlineSmall primary" @click="signUp" :disabled="loading">
                        <span v-if="!loading">
                            Sign up
                        </span>
                        <span v-else class="buttonLoader">
                            <IconBase width="30" height="16" iconName="loaderIcon"></IconBase>
                        </span>
                    </button>
                </div>
            </div>
        </div>
        <SignUpFooter></SignUpFooter>
    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import FormInput from 'components/inputs/formInput/FormInput.vue';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import consts from 'services/metaData/consts';
import { emailRegex, passwordRegexFunction } from 'services/utils/regex';
import captchaService from 'services/captcha';
import links from 'services/metaData/links';
import analytics from 'services/utils/analytics';
import { getNested } from 'services/utils/objectUtils';
import DropdownSearch from 'components/dropdowns/dropdownSearch.vue';
import CountriesService from 'services/metaData/countries';
import {
    MANDATORY_ERROR,
    PASSWORD_MATCH_ERROR,
    NO_SPECIAL_CHARS_ERROR,
    PASSWORD_VALIDATION_ERROR,
    EMAIL_FORMAT_ERROR
} from 'services/metaData/errors';
import SignUpFooter from './components/signUpFooter/signUpFooter.vue';
import Captcha from './components/captcha.vue';
import signUpService from './services/signUp';

export default {
    name: 'signUpLayout',
    components: {
        DropdownSearch,
        Captcha,
        IconBase,
        FormInput,
        SignUpFooter,
        SystemMessage
    },
    data() {
        return {
            consts,
            links,
            mixPanelShowFormSent: false,
            mandatoryError: MANDATORY_ERROR,
            loading: false,
            isCaptchaDisplayed: false,
            captchaResponse: null,
            captchaRequiredError: false,
            noSpecialCharsErr: NO_SPECIAL_CHARS_ERROR,
            passwordsMatchErr: PASSWORD_MATCH_ERROR,
            passwordValidationErr: PASSWORD_VALIDATION_ERROR,
            emailValidationErr: EMAIL_FORMAT_ERROR,
            emailRegex,
            passwordRegexFunction,
            errorMessage: '',
            showValidationErrors: false,
            userData: {},
            isPassHidden: true,
            isValid: {
                password: false,
                confirmPassword: false,
                country: false,
                email: false,
                firstName: false,
                lastName: false,
                companyName: false
            },
            countries: []
        };
    },
    async created() {
        this.isCaptchaDisplayed = await captchaService.checkDisplayCaptcha();
        this.countries = await CountriesService.get();
    },
    mounted() {
        this.$gtm.track('signup view');
        this.sendMixPanelShowForm();
    },
    updated() {
        this.sendMixPanelShowForm();
    },
    computed: {
        utmData() {
            const {
                // eslint-disable-next-line camelcase
                utm_medium, utm_source, utm_term, utm_content
            } = this.$route.query;

            return {
                utmMedium: utm_medium,
                utmSource: utm_source,
                utmTerm: utm_term,
                utmContent: utm_content
            };
        },
        referrerId() {
            return this.$route.query.cid;
        }
    },
    methods: {
        showPasswordChanged(value) {
            this.isPassHidden = value;
        },
        onCallbackCaptchaResponse(captchaResponse) {
            analytics.track('AccountSignUp', 'captchaSuccess');
            this.captchaResponse = captchaResponse;
            this.captchaRequiredError = false;
        },
        onCallbackExpiredCaptcha() {
            analytics.track('AccountSignUp', 'captchaExpired');
            this.captchaResponse = null;
        },
        async signUp() {
            analytics.track('AccountSignUp', 'signUpClick');
            if (this.isCaptchaDisplayed && !this.captchaResponse) {
                this.captchaRequiredError = true;
            }
            if (this.isCaptchaDisplayed) {
                analytics.track('AccountSignUp', 'captchaDisplayed');
                if (window.mixpanel && window.mixpanel.people !== undefined) {
                    window.mixpanel.people.set({ $captchaSignUp: true });
                }
            }

            if (!this.userData.termsAndConditions) {
                this.showValidationErrors = true;
                return false;
            }
            const keys = Object.keys(this.isValid);
            for (let i = 0; i < keys.length; i++) {
                if (!this.isValid[keys[i]]) {
                    this.showValidationErrors = true;
                    return false;
                }
            }

            if (window.mixpanel && window.mixpanel.alias !== undefined) {
                // link mix panel guid Id with new user id
                window.mixpanel.alias(this.userData.email);
                // update in mix panel when user sign up
                window.mixpanel.people.set({
                    $created: new Date(),
                    $name: this.userData.companyName
                });
            }
            this.loading = true;
            this.errorMessage = '';
            await signUpService.signUp({
                ...this.userData,
                userName: this.userData.email,
                utmData: this.utmData,
                referrerId: this.referrerId,
                captchaResponse: this.captchaResponse
            }).then(() => {
                this.$gtm.track('signup done');
                analytics.track('AccountSignUp', 'signUpSuccess');
                this.$router.push({ path: '/signup/sent', query: { email: this.userData.email } });
            }).catch((error) => {
                analytics.track('AccountSignUp', 'signUpError');
                this.errorMessage = getNested(error, 'response.data.error') || error.message;
                document.querySelector('.signUp').scrollTop = 0;
            }).finally(() => {
                this.loading = false;
            });
            return true;
        },
        inputValueChange({ isValid }, key) {
            this.isValid[key] = isValid;
        },
        countryDropdownValueChange(countryCode) {
            this.isValid.country = !!countryCode;
            this.userData.country = countryCode;
        },
        isOnlyAsciiChars(strToCheck) {
            if (!strToCheck) {
                return true;
            }
            for (let i = 0, n = strToCheck.length; i < n; i++) {
                if (strToCheck.charCodeAt(i) > 127) {
                    return false;
                }
            }
            return true;
        },
        checkNoNumeric(toCheck) {
            if (!toCheck) {
                return true;
            }
            return !toCheck.match(/[0-9]/);
        },
        passwordsMatch(passConfirm) {
            return passConfirm === this.userData.password;
        },
        sendMixPanelShowForm() {
            if (!window.mixpanel || this.mixPanelShowFormSent) {
                return;
            }

            this.mixPanelShowFormSent = true;
            const mixPanelData = {};
            if (this.referrerId) {
                mixPanelData.cid = this.referrerId;
                mixPanelData.cid_legacy = this.referrerId;
            }
            if (this.utmData) {
                if (this.utmData.utmSource) {
                    mixPanelData.utm_source = this.utmData.utmSource;
                }
                if (this.utmData.utmMedium) {
                    mixPanelData.utm_medium = this.utmData.utmMedium;
                }
                if (this.utmData.utmContent) {
                    mixPanelData.utm_content = this.utmData.utmContent;
                }
                if (this.utmData.utmTerm) {
                    mixPanelData.utm_term = this.utmData.utmTerm;
                }
            }
            if (this.referrerId && window.mixpanel && window.mixpanel.people !== undefined) {
                window.mixpanel.register(mixPanelData);
                window.mixpanel.people.set(mixPanelData);
            }
            analytics.track('AccountSignUp', 'signUpShowForm', mixPanelData);
        }
    }
};
</script>

<style>
/* fix fusion's icon-arrow clash with an arrow icon in ssa */
fusion-icon.icon-arrow {
    background-image: none;
}

/* fix fusion's firefox input required issue */
.is-input:invalid {
    box-shadow: none;
}
</style>

<style scoped lang="scss">
    @import "~styles/styleGuide.V2";
    @import "~styles/checkbox.V2";
    @import "~styles/buttons.V2";
    @import "~styles/links";
    @import "~styles/screens";

    .header {
        padding-top: 18px;
        display: grid;
        grid-template-columns: 33px 150px auto 161px 100px 30px;
        grid-template-rows: 37px;
        line-height: 37px;

        @media #{$mobile} {
            grid-template-columns: 33px 150px auto auto 100px 30px;
        }
    }

    .logo {
        grid-column-start: 2;
    }

    .signUp {
        color: $dark-500;
        background-color: $white;
        position: relative;

        button {
            outline: 0;
        }
    }

    .loginLabel {
        grid-column-start: 4;
         &.body {
             line-height: 37px;
         }

        @media #{$mobile} {
            display: none;
        }
    }

    .loginButton {
        display: inline-block;
        cursor: pointer;
        grid-column-start: 5;
        padding: 0 26px;
        border-radius: 19px;
        border: solid 1px $dark-500;
        color: $dark-500;
        background-color: $white;
        position: relative;
        overflow: hidden;
        font-size: 13px;
        line-height: 37px;

        &:hover {
            .loginButtonHovered-background {
                left: 0;
            }
            .loginButtonHovered-text {
                left: 0;
            }
        }
        .loginButtonHovered-text {
            padding: 0 26px;
            border-radius: 19px;
            white-space: nowrap;
            display: inline-block;
            text-align: center;
            position: absolute;
            color: $white;
            left: 100px;
            transition: left .3s ease;
        }
        .loginButtonHovered-background {
            transition: left .3s ease;
            left: -100px;
            width: 100px;
            height: 37px;
            display: inline-block;
            white-space: nowrap;
            position: absolute;
            background-color: $dark-500;
            overflow: hidden;
        }
    }

    .content {
        max-width: 1104px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-column-gap: 16px;
        margin-left: auto;
        margin-right: auto;
        margin-top: 25px;

        @media #{$tabletAndBelow} {
            margin-left: $spacing-xl;
            margin-right: $spacing-xl;
        }

        @media #{$mobile} {
            display: block;
        }
    }

    .growBusiness {
        line-height: 1.07;
        margin-top: 8px;
    }

    .spaceship {
        margin-left: -40px;
    }

    .form {
        margin-top: 60px;
        grid-template-columns: 1fr 1fr;
        display: grid;
        grid-column-gap: 32px;
        grid-row-gap: 16px;
        line-height: 20px;
    }

    .fullRow {
        grid-column: 1 / span 2;
    }

    .email {
        grid-row: 2;
    }

    .companyName {
        grid-row: 4;
    }

    .systemErrorMessage {
        grid-row: 6;
        .type-error {
            margin: $spacing-m 0;
        }
    }
    .noUpdate {
        grid-row: 7;
    }

    .termsAndConds {
        grid-row: 8;
    }

    .submitRow {
        grid-row: 9;
        margin-bottom: 100px;
        margin-top: 25px;
        button {
            width: 141px;
            float: right;
            margin-top: 19px;
        }
    }

    input[type='checkbox'] + label.checkboxLabel {
        color: $dark-700;
        font-weight: normal;
        width: calc(100% - 10px);
    }

    .error {
        position: absolute;
        top: 80px;
    }

    .captcha {
        display: inline-block;
    }
</style>
